import Backbone from 'backbone';
import dustjs from 'dustjs';
import { clientUtils } from 'app/client_utils';
import { dynamicYield } from 'app/dynamic_yield_utils';
import InlineRecsTileTemplate from 'templates/store/global/pages/categories/_rec_tile.dust';

// Button state names.
const LOADING = 'loading';
const LOADED = 'loaded';
const EXPANDED = 'expanded';

// Minimum amount of time (in milliseconds) to show the loading message.
const LOADING_MESSAGE_MIN_DURATION = 750;

// When showing/hiding recommendation tiles, how long to wait (in milliseconds) between each tile. Should be coordinated with show/hide animation duration.
const TILE_ANIMATION_STAGGER_TIME = 140;

// Number of recommendations to retrieve if not specified on initialization.
const DEFAULT_RECOMMENDATION_COUNT = 3;

export const InlineRecsView = Backbone.View.extend({
	events: {
		'click .js-inline-recs-btn': 'clickButton'
	},

	initialize(options) {
		this.parentView = options.parentView;
		this.strategyId = options.strategyId;
		this.maxProducts = options.maxProducts || DEFAULT_RECOMMENDATION_COUNT;
		this.exclusions = options.exclusions || { inlineRecs: true, productsOnPage: false };
		this._importAsyncTemplates();
	},

	_importAsyncTemplates() {
		import('./async_product_drop_templates').catch(window.clientErrorHandler);
	},

	/**
	 * Handle click on a "see similar" recommendations button.
	 * First click: load and show recommenation tiles.
	 * Second click: hide recommendations.
	 * Third click: show already loaded recommendations.
	 * @param {MouseEvent} event Click event.
	 * @returns {Promise} Promise indicating when the handler is complete. Mostly relevant when loading recommendations in the first place.
	 */
	clickButton(event) {
		event.preventDefault();
		const $button = $(event.currentTarget);

		// Ignore clicks when disabled, or in the middle of loading.
		if ($button.prop('disabled') || $button.data(LOADING)) {
			return;
		}

		// Check that we can retrieve recommendations before proceeding.
		const uniqueId = $button.data('uniqueid');
		const canRetrieveRecs = this.strategyId && uniqueId && dynamicYield.recommendationsAvailable();
		if (!canRetrieveRecs) {
			this.setButtonNoItems($button);
			return;
		}

		const loaded = $button.data(LOADED);
		if (!loaded) {
			$button.data(LOADING, true);
			this.trackInlineRecsLoad();

			// Show loading button, and start a timer for the minimum duration it needs to be shown.
			this.setButtonLoading($button);
			const loadingMessageDone = new Promise((resolve) => setTimeout(resolve, LOADING_MESSAGE_MIN_DURATION));

			return this.loadRecommendations(uniqueId, $button)
				.then((tiles) => {
					$button.data(LOADED, true);

					if (!tiles || !tiles.length) {
						return loadingMessageDone.then(() => {
							this.setButtonNoItems($button);
							$button.data(LOADING, false);
						});
					}

					// Show the new tiles.
					this.revealTiles(tiles);
					$button.data(EXPANDED, true);

					// Wait for loading message to be done, then show the hide button.
					return loadingMessageDone.then(() => {
						this.setButtonHide($button);
						$button.data(LOADING, false);
					});
				})
				.catch((error) => {
					window.clientErrorHandler(error);
					return loadingMessageDone.then(() => {
						this.setButtonNoItems($button);
						$button.data(LOADING, false);
					});
				});
		}

		const $tiles = this.findTilesFromSource(uniqueId);
		$tiles.finish(); // Complete any previously running animations.

		const expanded = $button.data(EXPANDED);
		if (expanded) {
			$button.data(EXPANDED, false);
			this.setButtonInitial($button);
			this.hideTiles($tiles.get());
		} else {
			$button.data(EXPANDED, true);
			this.setButtonHide($button);
			this.revealTiles($tiles.get());
		}
	},

	/**
	 * Load recommendation data, render it into tiles, and inject those tiles into the page after the reference product.
	 * @param {number} uniqueId ID of the product to load recommendations for.
	 * @param {JQuery} $button Selector of the button that triggered the recommendations load.
	 * @returns {Promise} Promise holding an array of selectors for the recommendation tiles injected into the page.
	 */
	loadRecommendations(uniqueId, $button) {
		const exclude = this.getRecsExclusions(this.exclusions);
		const $injectAfter = $button.closest('.js-product-wrapper');

		return dynamicYield
			.getProductRecommendationData(this.strategyId, uniqueId, { maxProducts: this.maxProducts, exclude })
			.then((data) => this.renderTiles(uniqueId, data))
			.then((tiles) => this.injectTiles(tiles, $injectAfter));
	},

	/**
	 * Take recommendation data and render it into inline recommendation tiles.
	 * @param {number} uniqueId ID of the product the recommendations are for.
	 * @param {object[]} data Recommendation data.
	 * @returns {Promise} Promise holding an array of rendered recommendation tile markup.
	 */
	renderTiles(uniqueId, data) {
		if (!data || !data.slots || !data.slots.length) {
			return Promise.resolve(null);
		}

		// Translate recommendation data for use with product tile template.
		const productTiles = data.slots.map((slot) => dynamicYield.recommendationSlotToProductTile(slot));
		const renderPromises = productTiles.map((tile) => {
			const recommendationContext = {
				...tile.recommendationContext,
				widgetId: data.wId,
				feedId: data.fId
			};
			const dustData = {
				product: tile.product,
				recommendationContext,
				sourceId: uniqueId,
				view: this.parentView.view,
				isListView: window.dataLayer.isListView,
				device: window.BCOM.device,
				isBuild: window.BCOM.theme.isBuild,
				BCOM: window.BCOM,
				isQuickShipEnabled: window.dataLayer.isQuickShipEnabled,
				spacerImage: '/public/shared/images/transparent.png'
			};
			return new Promise((resolve, reject) => {
				dustjs.render(InlineRecsTileTemplate, dustData, (error, markup) => {
					if (error) {
						reject(error);
					} else {
						resolve(markup);
					}
				});
			});
		});
		return Promise.all(renderPromises);
	},

	/**
	 * Inject recommendation tile markup into the page at a given point.
	 * @param {string[]} tiles Array of recommendation tile markup.
	 * @param {jQuery} $injectAfter Tiles will be injected after this selector, generally the product that the recommendations are for.
	 * @returns {jQuery[]} Array of selectors representing the injected tiles.
	 */
	injectTiles(tiles, $injectAfter) {
		if (!tiles || !tiles.length) {
			return [];
		}

		// Add tiles to the page, initially collapsed.
		let $currentInjectAfter = $injectAfter;
		return tiles.map((tile) => {
			const $tile = $(tile);
			$tile.addClass('collapse-tile');
			$currentInjectAfter.after($tile);
			$currentInjectAfter = $tile;

			// Register recommendation with Dynamic Yield for tracking.
			dynamicYield.registerRecommendationPlacement($tile[0]);

			// Hook up compare button.
			window.bodyView.baseView.compareWidgetView.initButtons($tile);

			return $tile;
		});
	},

	/**
	 * Show tiles with a staggered reveal animation.
	 * @param {object[]} tiles Array of tile elements to be shown.
	 */
	revealTiles(tiles) {
		this.staggeredForEach(tiles, TILE_ANIMATION_STAGGER_TIME, (tile) => $(tile).removeClass('collapse-tile'));
	},

	/**
	 * Hide tiles with a staggered hide animation.
	 * @param {object[]} tiles Array of tile elements to be hidden.
	 */
	hideTiles(tiles) {
		this.staggeredForEach(tiles.reverse(), TILE_ANIMATION_STAGGER_TIME, (tile) => $(tile).addClass('collapse-tile'));
	},

	/**
	 * Run a callback function for each element in an array, with a specified delay between each element.
	 * @param {object[]} elements Array of elements to be looped over.
	 * @param {number} delay Number of milliseconds between the callback for each item.
	 * @callback callback Function to be called for each item in the elements array.
	 * @param {object} element Current element in the loop.
	 * @param {number} index Index of current element.
	 */
	staggeredForEach(elements, delay, callback) {
		elements.forEach((element, index) => {
			// Using jQuery animate here to 1) stagger the callback on each element via animation duration, and 2) allow finishing calls early with $(element).finish().
			// Note that we don't actually want to animate anything, so we just make up a custom property name. jQuery don't care.
			$(element).animate({ '--staggered-for-each': 0 }, { duration: index * delay, always: () => callback(element, index) });
		});
	},

	/**
	 * Find recommendation tiles for a source product.
	 * @param {number} uniqueId ID of the source product.
	 */
	findTilesFromSource(uniqueId) {
		return this.$('.js-inline-rec').filter((index, element) => $(element).data('sourceId') === uniqueId);
	},

	/**
	 * Track loading of inline reommendations.
	 */
	trackInlineRecsLoad() {
		const trackingData = {
			props: {
				prop46: 'inlineRecsProductDisplay'
			},
			linkName: 'Product Drop Show Inline Recommendations Click'
		};
		Backbone.trigger('track:link', trackingData);
	},

	/**
	 * Track click-through on an inline recommendation.
	 * @param {MouseEvent} event Click event.
	 */
	trackInlineRecClick(event) {
		event.preventDefault();
		const trackingData = {
			evars: {
				eVar76: 'inlineRecsProductClick'
			},
			linkName: 'Product Drop Inline Recommendation Click',
			redirect: event.currentTarget.href
		};
		Backbone.trigger('track:link', trackingData);
	},

	/**
	 * Create a unique ID (SKU) exclusion list to filter out already-loaded products from recommendations.
	 * Can find previously-loaded inline recommendations, normal product tiles on the page, or both.
	 * @param {object} options Categories of items to exclude.
	 * @returns {object} Exclusion specification to be used with the recommendation API.
	 */
	getRecsExclusions(options = {}) {
		let excludeSkus = [];

		if (options.inlineRecs) {
			const strategyId = this.strategyId;
			const recSkus = clientUtils
				.flattenArray(
					dynamicYield
						.getLoadedWidgets()
						.filter((widget) => widget.wId === strategyId)
						.map((widget) => widget.slots)
				)
				.map((slot) => slot.item.sku);
			excludeSkus = excludeSkus.concat(recSkus);
		}

		if (options.productsOnPage && Array.isArray(window.dataLayer.products)) {
			const productOnPageSkus = window.dataLayer.products.map((product) => `${product.uniqueId}`);
			excludeSkus = excludeSkus.concat(productOnPageSkus);
		}

		return excludeSkus.length ? { sku: excludeSkus } : undefined;
	},

	/**
	 * Filter function for jQuery .removeClass() to remove "theme" classes such as "theme-white" and "bg-theme-grey".
	 * @param {number} index Index in the jQuery selector set.
	 * @param {string} className Full className property string.
	 */
	removeThemeClasses(index, className) {
		// This regex matches "theme" as a whole word, without matching other strings simply containing it.
		// Example: matches "theme-primary", but not "unthemed" or "mycustomtheme".
		return clientUtils
			.splitClassName(className)
			.filter((name) => /\btheme\b/.test(name))
			.join(' ');
	},

	/**
	 * Filter function for jQuery .removeClass() to remove "rotate" classes such as "rotate-90" and "rotate-180".
	 * @param {number} index Index in the jQuery selector set.
	 * @param {string} className Full className property string.
	 */
	removeRotateClasses(index, className) {
		return clientUtils
			.splitClassName(className)
			.filter((name) => name.indexOf('rotate-') === 0)
			.join(' ');
	},

	/**
	 * Generate a space-separated string of the given classes, plus the same class names with "hover-" and "active-" prefixes.
	 * @param {string} classNames Whitespace-separated classes to generate variations for.
	 */
	getPseudoClassVariations(className) {
		return clientUtils
			.splitClassName(className)
			.map((name) => `${name} hover-${name} active-${name}`)
			.join(' ');
	},

	/**
	 * Remove theming from a "see similar" button. Used before adding new styles to represent different states of the button.
	 * @param {jQuery} $button Selector of button to remove styles for.
	 */
	stripButtonStyles($button) {
		$button.removeClass(this.removeThemeClasses);
		$button
			.find('.js-button-icon')
			.removeClass(this.removeThemeClasses)
			.removeClass(this.removeRotateClasses)
			.removeClass('ba bw1 mr2');
	},

	/**
	 * Revert similar recs button style to initial "see similar" state.
	 * @param {jQuery} $button Selector of button to style.
	 */
	setButtonInitial($button) {
		this.stripButtonStyles($button);
		$button.removeClass('expanded').addClass('theme-grey-medium bg-theme-grey-light b--theme-grey-medium');
		$button.find('.js-button-icon').addClass('theme-grey-medium bg-theme-grey-light');
		this.animateInlineRecButtonTextChange($button, 'See Similar');
	},

	/**
	 * Style similar recs button for loading state.
	 * @param {jQuery} $button Selector of button to style.
	 */
	setButtonLoading($button) {
		this.stripButtonStyles($button);
		$button.addClass('expanded theme-grey-light bg-theme-grey-medium b--theme-grey-medium');
		$button.find('.js-button-icon').addClass('theme-grey-medium bg-theme-grey-light ba bw1 mr2 rotate-90');
		this.animateInlineRecButtonTextChange($button, 'Loading...');
	},

	/**
	 * Style similar recs button for loaded state with tiles shown.
	 * @param {jQuery} $button Selector of button to style.
	 */
	setButtonHide($button) {
		this.stripButtonStyles($button);
		$button.addClass('expanded').addClass('theme-white bg-theme-primary-dark b--theme-primary-dark');
		$button.find('.js-button-icon').addClass('theme-primary-dark bg-theme-white b--theme-primary-dark ba bw1 mr2 rotate-180');
		this.animateInlineRecButtonTextChange($button, 'Similar Items');
	},

	/**
	 * Style similar recs button for "no items" state.
	 * @param {jQuery} $button Selector of button to style.
	 */
	setButtonNoItems($button) {
		$button.prop('disabled', true);
		this.stripButtonStyles($button);
		$button
			.addClass('expanded pl2')
			.addClass(this.getPseudoClassVariations('theme-grey-medium bg-theme-grey-light b--theme-grey-medium'));
		$button
			.find('.js-button-icon')
			.css('width', 0)
			.children()
			.css('width', 0);
		this.animateInlineRecButtonTextChange($button, 'No Items Found');
	},

	/**
	 * Animate a change of text for a similar recs button.
	 * @param {jQuery} $button Selector of button to change text for.
	 * @param {string} newText Text to put into the button.
	 */
	animateInlineRecButtonTextChange($button, newText) {
		const $text = $button.find('.js-button-text');

		// Record current width before text change.
		const oldTextWidth = $text.outerWidth();

		// Change text, and set width to auto so it will resize.
		$text.text(newText);
		$text.width('auto');

		// Record auto-sized width.
		const newTextWidth = $text.outerWidth();

		// Reset to old width, then change to new width to trigger CSS transition.
		$text.width(oldTextWidth);
		$text.width(newTextWidth);
	}
});
